import { useState, useMemo } from 'react';
import { LOJAS_AUDITAVEIS, lojasDoMercado, mercadoDoConsultor } from '../../utils/empresas';

// Cor de identidade do módulo Operacional (a mesma do ModuloHeader do hub).
const COR_OPERACIONAL = '#475569';

/**
 * Estado do filtro de MERCADO (Norte × Sul) das avaliações do Operacional.
 *
 * Cross-avaliação (DEM-00019): o consultor designado (ID 21/22) só avalia o
 * mercado do OUTRO. Aqui é só UX — o portão de verdade é o backend
 * (server/operacional/consultor-mercado.js); se divergirem, o backend manda.
 *
 * @param {boolean} ativo  liga o filtro. A SA liga; a TSV (mesma tela) não.
 * @returns {{ativo, travaMercado, filtroMercado, setFiltroMercado, lojas}}
 *   - travaMercado: 'Norte'|'Sul' se o usuário é consultor designado; senão null
 *   - filtroMercado: '' (Todos) | 'Norte' | 'Sul' — escolha do perfil amplo
 *   - lojas: a lista de lojas já recortada para o dropdown
 */
export function useFiltroMercado(ativo = true) {
  // Consultor designado? Sai do usuário no localStorage (id). Uma vez só.
  const travaMercado = useMemo(() => {
    if (!ativo) return null;
    try {
      const u = JSON.parse(localStorage.getItem('user')) || {};
      return mercadoDoConsultor(u.id);
    } catch {
      return null;
    }
  }, [ativo]);

  const [filtroMercado, setFiltroMercado] = useState('');
  // O consultor travado ignora o toggle; o amplo manda pelo filtroMercado.
  const mercadoAtivo = travaMercado || filtroMercado || null;
  const lojas = useMemo(
    () => (mercadoAtivo ? lojasDoMercado(mercadoAtivo) : LOJAS_AUDITAVEIS),
    [mercadoAtivo],
  );

  return { ativo, travaMercado, filtroMercado, setFiltroMercado, lojas };
}

/**
 * A UI do filtro. Consultor travado vê um selo do mercado que avalia (sem escolha); o
 * perfil amplo vê o toggle Todos / Norte / Sul. Controlado pelo pai: `onChange`
 * recebe o mercado novo ('' = Todos) e o pai reseta a loja selecionada.
 */
export function FiltroMercado({ ativo = true, travaMercado, filtroMercado, onChange, cor = COR_OPERACIONAL }) {
  if (!ativo) return null;

  const rotulo = { fontSize: 12, fontWeight: 800, color: '#475569', letterSpacing: 0.3 };

  // Consultor designado: mercado fixo (o do OUTRO), sem toggle.
  if (travaMercado) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={rotulo}>AVALIANDO</span>
        <span style={{ padding: '4px 12px', borderRadius: 999, background: cor, color: '#fff', fontWeight: 800, fontSize: 13 }}>
          {travaMercado}
        </span>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>(suas lojas designadas)</span>
      </div>
    );
  }

  // Perfil amplo: toggle Todos / Norte / Sul (começa em Todos).
  const opcoes = [['', 'Todos'], ['Norte', 'Norte'], ['Sul', 'Sul']];
  const btn = (selecionado) => ({
    padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13,
    border: `2px solid ${selecionado ? cor : '#e2e8f0'}`,
    background: selecionado ? cor : '#fff',
    color: selecionado ? '#fff' : '#475569',
    transition: 'all .12s',
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
      <span style={rotulo}>MERCADO</span>
      {opcoes.map(([val, label]) => (
        <button key={val || 'todos'} type="button" onClick={() => onChange(val)} style={btn(filtroMercado === val)}>
          {label}
        </button>
      ))}
    </div>
  );
}
